import { XCircle } from 'lucide-react';
import { format, isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { Message } from './types';

function formatMessageTime(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    if (isValid(date)) {
      return format(date, 'HH:mm');
    }
    return '';
  } catch {
    return '';
  }
}

/** Bubble frame: alignment, footer (time + type + status ticks), failed row, reaction badge. */
export function MessageBubble({ message, children }: { message: Message; children: React.ReactNode }) {
  const { t } = useTranslation('conversations');

  const reactionEmojis = [message.reactions?.customer?.emoji, message.reactions?.business?.emoji].filter(
    Boolean,
  );

  return (
    <div
      className={cn('flex mb-2', message.direction === 'outbound' ? 'justify-end' : 'justify-start')}
    >
      <div
        className={cn(
          'max-w-[70%] rounded-lg px-3 py-2 relative shadow-sm',
          message.direction === 'outbound'
            ? 'bg-chat-bubble-out text-chat-text rounded-br-none'
            : 'bg-chat-bubble-in text-chat-text rounded-bl-none',
          reactionEmojis.length > 0 && 'mb-2',
        )}
      >
        {children}

        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[11px] text-chat-text-muted">{formatMessageTime(message.createdAt)}</span>

          {message.messageType && (
            <span className="text-[11px] text-chat-text-muted opacity-60">
              ·{' '}
              {t(`messageView.messageTypes.${message.messageType}`, {
                defaultValue: message.messageType,
              })}
            </span>
          )}

          {message.direction === 'outbound' && message.status && (
            <>
              {message.status === 'failed' ? (
                <XCircle className="h-3.5 w-3.5 text-red-500 dark:text-red-400" />
              ) : message.status === 'read' ? (
                <span className="text-xs text-chat-receipt-read">✓✓</span>
              ) : message.status === 'delivered' ? (
                <span className="text-xs text-chat-text-muted">✓✓</span>
              ) : message.status === 'sent' || message.status === 'pending' ? (
                <span className="text-xs text-chat-text-muted">✓</span>
              ) : null}
            </>
          )}
        </div>

        {message.direction === 'outbound' && message.status === 'failed' && (
          <div className="mt-1">
            <span className="text-[11px] text-red-500 dark:text-red-400 flex items-center gap-1">
              {t('messageView.notDelivered')}
            </span>
          </div>
        )}

        {reactionEmojis.length > 0 && (
          <div className="absolute -bottom-2 -right-2 bg-chat-panel rounded-full px-1.5 py-0.5 text-sm shadow-sm border">
            {reactionEmojis.join(' ')}
          </div>
        )}
      </div>
    </div>
  );
}
